import componentManifestData from "@/lib/debug/component-manifest.json";
import type { SiblingTab, TabSource } from "../debug-panel.types";

// Ownership wrappers to skip: our ui/tabs.tsx wrappers + immediate Radix primitives
export const SKIP_COMPONENT_NAMES = new Set([
  "TabsTrigger",
  "TabsList",
  "Tabs",
  "TabsContent",
  "Trigger",
  "List",
  "Root",
  "Content",
]);

export const isInternalFile = (f: string) =>
  f.includes("/node_modules/") ||
  f.includes("\\node_modules\\") ||
  f.includes("/components/ui/") ||
  f.includes("\\components\\ui\\");

export const componentMap = componentManifestData.components as Record<
  string,
  string | string[]
>;

// Unwrap React.memo, React.forwardRef, and React.lazy wrappers
// so we can read the actual component's .name and .displayName.
export function resolveComponentType(type: unknown):
  | (((...args: unknown[]) => unknown) & {
      displayName?: string;
      name?: string;
    })
  | null {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-return -- React function type cast
  if (typeof type === "function") return type as any;
  if (!type || typeof type !== "object") return null;
  const t = type as Record<string, unknown>;
  // React.memo / React.forwardRef
  if (t["type"]) {
    const inner = resolveComponentType(t["type"]);
    if (inner) return inner;
  }
  if (t["render"]) {
    const inner = resolveComponentType(t["render"]);
    if (inner) return inner;
  }
  // React.lazy — resolve once the promise has settled (_status === 1)
  if (t["_payload"] && typeof t["_payload"] === "object") {
    const payload = t["_payload"] as Record<string, unknown>;
    if (payload["_status"] === 1 && payload["_result"]) {
      const result = payload["_result"] as Record<string, unknown>;
      const inner = resolveComponentType(
        typeof result["default"] !== "undefined" ? result["default"] : result,
      );
      if (inner) return inner;
    }
  }
  return null;
}

// Normalize a React fiber _debugSource.fileName (absolute path) to a
// project-relative path under app/ | components/ | lib/ | hooks/ | providers/.
// Returns null when the path is internal (node_modules, components/ui) or
// doesn't match any recognized top-level source directory.
export function extractDebugSourceFile(raw: string | undefined): string | null {
  if (!raw || isInternalFile(raw)) return null;
  const m = raw.match(
    /[/\\](app[/\\].+|components[/\\].+|lib[/\\].+|hooks[/\\].+|providers[/\\].+)$/,
  );
  return m?.[1]?.replace(/\\/g, "/") ?? null;
}

// When a component name maps to multiple files (manifest conflict), pick the
// candidate whose path segments overlap most with the current URL pathname.
// Scoring rules:
//   +1 per URL segment that appears in the candidate path
//   -0.5 for barrel re-export files (index.ts / index.tsx) so concrete
//        component files win on ties
//   -10 for test/demo preview paths (anything containing /test-XXX/) when the
//       current URL is NOT itself on a test-* route. Prevents the manifest's
//       first-entry bias from attributing production renders to test pages
//       that happen to define a component with the same name.
export function resolveManifestPath(
  entry: string | string[],
  pathname: string,
): string {
  if (typeof entry === "string") return entry;
  if (entry.length === 1) return entry[0] ?? "";
  const segments = pathname.split("/").filter(Boolean);
  const onTestRoute = segments.some((s) => s.startsWith("test-"));
  let bestPath = entry[0] ?? "";
  let bestScore = -Infinity;
  for (const candidate of entry) {
    let score = 0;
    for (const seg of segments) {
      if (candidate.includes(seg)) score++;
    }
    if (/\/index\.(ts|tsx)$/.test(candidate)) score -= 0.5;
    if (!onTestRoute && /\/test-[^/]+\//.test(candidate)) score -= 10;
    if (score > bestScore) {
      bestScore = score;
      bestPath = candidate;
    }
  }
  return bestPath;
}

// Extract only the label text from a tab trigger, ignoring badge counts and SVG icons.
export function getTabLabel(el: HTMLElement): string {
  const clone = el.cloneNode(true) as HTMLElement;
  clone.querySelectorAll('[data-slot="badge"]').forEach((n) => n.remove());
  return clone.textContent?.trim() ?? "";
}

// Walk the React _debugOwner chain to find the user component that renders <TabsTrigger>.
export function getAutoSource(
  el: HTMLElement,
  pathname: string,
): TabSource | undefined {
  if (process.env.NODE_ENV !== "development") return undefined;
  const fiberKey = Object.keys(el).find((k) => k.startsWith("__reactFiber$"));
  if (!fiberKey) return undefined;
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-member-access -- React internal fiber API
  let owner = (el as any)[fiberKey]?._debugOwner;
  while (owner) {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access -- React internal fiber API
    const raw: string | undefined = owner._debugSource?.fileName;
    const fromSource = extractDebugSourceFile(raw);
    if (fromSource) return { kind: "file", path: fromSource };
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access -- React internal fiber API
    const resolved = resolveComponentType(owner.type);
    if (resolved) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access -- React internal fiber API
      const outerDisplayName = owner.type?.displayName as string | undefined;
      const name: string =
        outerDisplayName || resolved.displayName || resolved.name || "";
      if (name && !SKIP_COMPONENT_NAMES.has(name) && !/^[a-z]/.test(name)) {
        const entry = componentMap[name];
        if (entry) {
          const resolved_path = resolveManifestPath(entry, pathname);
          if (!isInternalFile(resolved_path)) {
            return { kind: "file", path: resolved_path };
          }
        }
      }
    }
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access -- React internal fiber API
    owner = owner._debugOwner;
  }
  return undefined;
}

export function sourcesEqual(
  a: TabSource | undefined,
  b: TabSource | undefined,
): boolean {
  if (!a || !b) return false;
  if (a.kind !== b.kind) return false;
  if (a.kind === "file" && b.kind === "file") return a.path === b.path;
  if (a.kind === "component" && b.kind === "component")
    return a.name === b.name;
  return false;
}

// Walk the React fiber CHILD tree from inside the active TabsContent
// to find the first user component in the manifest.
export function getTabContentSource(
  triggerEl: HTMLElement,
  pathname: string,
): TabSource | undefined {
  if (process.env.NODE_ENV !== "development") return undefined;
  const hintName = triggerEl.dataset.debugComponent;
  if (hintName) {
    const hintEntry = componentMap[hintName];
    if (hintEntry) {
      return { kind: "file", path: resolveManifestPath(hintEntry, pathname) };
    }
  }
  const tabsEl = triggerEl.closest<HTMLElement>('[data-slot="tabs"]');
  if (!tabsEl) return undefined;
  const activeContent = tabsEl.querySelector<HTMLElement>(
    '[data-slot="tabs-content"][data-state="active"]',
  );
  if (!activeContent) return undefined;
  const fiberKey = Object.keys(activeContent).find((k) =>
    k.startsWith("__reactFiber$"),
  );
  if (!fiberKey) return undefined;
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-member-access -- React internal fiber API
  const startFiber = (activeContent as any)[fiberKey]?.child;
  if (!startFiber) return undefined;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- React internal fiber API
  const stack: any[] = [startFiber];
  let checked = 0;
  while (stack.length > 0 && checked < 50) {
    checked++;
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment -- React internal fiber API
    const fiber = stack.pop();
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access -- React internal fiber API
    const resolved = resolveComponentType(fiber.type);
    if (resolved) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access -- React internal fiber API
      const outerDisplayName = fiber.type?.displayName as string | undefined;
      const name: string =
        outerDisplayName || resolved.displayName || resolved.name || "";
      if (name && !SKIP_COMPONENT_NAMES.has(name) && !/^[a-z]/.test(name)) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-assignment -- React internal fiber API
        const rawSource: string | undefined = fiber._debugSource?.fileName;
        const fromSource = extractDebugSourceFile(rawSource);
        const entry = componentMap[name];
        const path =
          fromSource ?? (entry ? resolveManifestPath(entry, pathname) : null);
        if (path && !path.startsWith("components/ui/")) {
          return { kind: "file", path };
        }
      }
    }
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access -- React internal fiber API
    if (fiber.sibling) stack.push(fiber.sibling);
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access -- React internal fiber API
    if (fiber.child) stack.push(fiber.child);
  }
  return undefined;
}

// Resolve a data-debug-component attribute value to a manifest file path.
export function resolveDebugComponent(
  el: HTMLElement,
  pathname: string,
): string | undefined {
  const name = el.dataset.debugComponent;
  if (!name) return undefined;
  const entry = componentMap[name];
  return entry ? resolveManifestPath(entry, pathname) : undefined;
}

// Returns true if the element is inside an inactive TabsContent.
export function isInsideInactiveTabsContent(el: HTMLElement): boolean {
  let node: HTMLElement | null = el.parentElement;
  while (node) {
    if (
      node.dataset.slot === "tabs-content" &&
      node.dataset.state === "inactive"
    )
      return true;
    node = node.parentElement;
  }
  return false;
}

export type { TabSource, SiblingTab };
